import { Injectable } from "@angular/core";
import { CommonApiService } from "./common-api.service";
import { APIURL } from "../global/constant";

@Injectable({
  providedIn: "root",
})
export class ExternalShareService {
  constructor(private commonApiService: CommonApiService) { }

  create(payload: {
    campaign_id: string;
    role_id: string | null;
    asset_type: string;
    asset_id: string;
    video_id: string;
    email_users: { firstname: string; lastname: string; email: string }[];
    user_ids: number[];
    user_note: string;
    action: string;
  }) {
    return this.commonApiService.post(APIURL.externalShareCreate, payload);
  }

  cancelRequest(payload: {
    campaign_id: string;
    role_id: string | null;
    asset_type: string;
    asset_id: string;
    video_id: string;
    cancel_note: string;
  }) {
    return this.commonApiService.post(
      APIURL.externalShareCancelRequest,
      payload,
    );
  }

  sendReminder(payload: {
    asset_type: string;
    asset_id: string;
    campaign_id: string;
    video_id: string;
    reminder_note: string;
  }) {
    return this.commonApiService.post(APIURL.externalShareSendReminder, payload);
  }

  heartbeat(payload: { token: string; share_id: string; asset_name: string }) {
    return this.commonApiService.post(APIURL.externalShareHeartbeat, payload);
  }

  login(payload: {
    login_code: string;
    email: string;
    share_id: string;
    asset_type: string;
  }) {
    return this.commonApiService.post(APIURL.externalShareLogin, payload);
  }

  getDetails(params: { share_id: string; asset_name: string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareDetails, httpParams);
  }

  /** Users with access / approvers for an asset (query: asset_type, asset_id). */
  getAccessUsers(params: { asset_type: string; asset_id: string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareAccessUsers, httpParams);
  }

  getConcept(shareId: string) {
    return this.commonApiService.get(
      `${APIURL.externalShareConceptGet}/${shareId}`,
    );
  }

  getScript(shareId: string) {
    return this.commonApiService.get(
      `${APIURL.externalShareScriptGet}/${shareId}`,
    );
  }

  getMoodboard(shareId: string) {
    return this.commonApiService.get(
      `${APIURL.externalShareMoodboardGet}/${shareId}`,
    );
  }

  getMoodboardV2(shareId: string) {
    return this.commonApiService.get(
      `${APIURL.externalShareMoodboardGetV2}/${shareId}`,
    );
  }

  getStoryboard(
    shareId: string,
    options?: { campaign_video_aspect_ratio_id?: number | string },
  ) {
    const httpParams =
      options?.campaign_video_aspect_ratio_id != null
        ? this.commonApiService.getParamsFromObj({
            campaign_video_aspect_ratio_id:
              options.campaign_video_aspect_ratio_id,
          })
        : undefined;
    return this.commonApiService.get(
      `${APIURL.externalShareStoryboardGet}/${shareId}`,
      httpParams,
    );
  }

  getVideo(
    shareId: string,
    options?: { campaign_video_aspect_ratio_id?: number | string },
  ) {
    const httpParams =
      options?.campaign_video_aspect_ratio_id != null
        ? this.commonApiService.getParamsFromObj({
            campaign_video_aspect_ratio_id:
              options.campaign_video_aspect_ratio_id,
          })
        : undefined;
    return this.commonApiService.get(
      `${APIURL.externalShareVideoGet}/${shareId}`,
      httpParams,
    );
  }

  getCampaignVideoAspectRatios(videoId: string | number) {
    const httpParams = this.commonApiService.getParamsFromObj({
      video_id: videoId,
    });
    return this.commonApiService.get(
      APIURL.externalShareAspectRatios,
      httpParams,
    );
  }

  getSound(shareId: string) {
    return this.commonApiService.get(
      `${APIURL.externalShareSoundGet}/${shareId}`,
    );
  }

  getAssetFeedbackList(params: { asset_type: string; asset_id: string; draft_id?: string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(
      APIURL.externalShareAssetFeedbackList,
      httpParams,
    );
  }

  getAssetFeedbackCounts(params: { asset_type: string; asset_id: string; draft_id?: string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(
      APIURL.externalShareAssetFeedbackCounts,
      httpParams,
    );
  }

  sendAssetFeedback(formData: FormData) {
    return this.commonApiService.post(
      APIURL.externalShareAssetFeedbackSend,
      formData,
    );
  }

  updateAssetFeedback(payload: { feedback_id: string; body: string }) {
    return this.commonApiService.post(
      APIURL.externalShareAssetFeedbackUpdate,
      payload,
    );
  }

  deleteAssetFeedback(feedbackId: string) {
    return this.commonApiService.post(
      `${APIURL.externalShareAssetFeedbackDelete}/${feedbackId}`,{}
    );
  }

  resolveAssetFeedback(payload: { feedback_id: string; status: string }) {
    return this.commonApiService.post(
      APIURL.externalShareAssetFeedbackResolve,
      payload,
    );
  }

  toggleReaction(payload: { feedback_id: string; emoji: string }) {
    return this.commonApiService.post(
      APIURL.externalShareAssetFeedbackReactionToggle,
      payload,
    );
  }

  markFeedbackAsRead(payload: { feedback_ids: (string | number)[] }) {
    return this.commonApiService.post(
      APIURL.externalShareAssetFeedbackRead,
      payload,
    );
  }

  getTimeline(params: { moodboard_id?: number | string; script_id?: number | string; draft_id?: number | string; entity_id?: number | string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareTimeline, httpParams);
  }

  getScriptTimeline(params: { script_id: number | string; draft_id: number | string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareTimelineScript, httpParams);
  }

  getShotlistTimeline(params: { shotlist_id: number | string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareTimelineShotlist, httpParams);
  }

  getVideoxTimeline(params: { videox_id: number | string }) {
    const httpParams = this.commonApiService.getParamsFromObj(params);
    return this.commonApiService.get(APIURL.externalShareTimelineVideox, httpParams);
  }

  getMoodboardPdfData(moodboardId: number | string) {
    return this.commonApiService.get(`${APIURL.externalShareMoodboardPdfData}/${moodboardId}`);
  }

  getStoryboardPdfData(
    storyboardId: number | string,
    options?: { campaign_video_aspect_ratio_id?: number | string },
  ) {
    const httpParams =
      options?.campaign_video_aspect_ratio_id != null
        ? this.commonApiService.getParamsFromObj({
            campaign_video_aspect_ratio_id:
              options.campaign_video_aspect_ratio_id,
          })
        : undefined;
    return this.commonApiService.get(
      `${APIURL.externalShareStoryboardPdfData}/${storyboardId}`,
      httpParams,
    );
  }

  saveShotlistFeedback(payload: any) {
    return this.commonApiService.post(APIURL.externalShareShotlistFeedbackSave, payload);
  }

  saveAivisualFeedback(payload: any) {
    return this.commonApiService.post(APIURL.externalShareAivisualFeedbackSave, payload);
  }

  saveScriptFeedback(payload: {
    script_id: number | string;
    draft_id: number | string;
    conversation_id: number | string | null;
    feedback_items: any[];
  }) {
    return this.commonApiService.post(
      APIURL.externalShareScriptFeedbackSave,
      payload,
    );
  }

  saveMoodboardFeedback(payload: {
    moodboard_id: number | string;
    conversation_id: number | string;
    video_id: number | string;
    campaign_id: number | string;
    entities: any[];
  }) {
    return this.commonApiService.post(
      APIURL.externalShareMoodboardFeedbackSave,
      payload,
    );
  }

  requestFeedbackChange(payload: {
    conversation_id: number | string;
    change_instruction: string;
    draft_id: number | string;
    video_id: number | string;
    asset_id: number | string;
  }) {
    return this.commonApiService.post(
      APIURL.externalShareFeedbackChatRequestChange,
      payload,
    );
  }

  getFeedbackChatMessages(conversationId: number | string) {
    const httpParams = this.commonApiService.getParamsFromObj({
      conversation_id: conversationId,
    });
    return this.commonApiService.get(
      APIURL.externalShareFeedbackChatMessages,
      httpParams,
    );
  }

  sendFeedbackChat(payload: {
    conversation_id: number | string;
    message: string;
    asset_type: string;
    asset_id: number | string;
    draft_id: number | string;
    video_id: number | string;
    campaign_id?: number | string;
    entity_id?: number | string | null;
    shot_id?: number | string | null;
    storyboard_image_id?: number | string | null;
    ai_video_id?: number | string | null;
  }) {
    return this.commonApiService.post(
      APIURL.externalShareFeedbackChatSend,
      payload,
    );
  }

  onAssetApproved(payload: any) {
    return this.commonApiService.post(
      APIURL.externalShareAssetApproved,
      payload,
    );
  }

  generateLink(payload: { asset_type: string; asset_id: number | string; video_id: number | string }) {
    return this.commonApiService.post(APIURL.externalShareGenerateLink, payload);
  }

  onEntityRefImageApproval(payload: any) {
    return this.commonApiService.post(
      APIURL.externalEntityRefImageApproved,
      payload,
    );
  }

  onShotImageApproval(payload: any) {
    return this.commonApiService.post(APIURL.externalShotImageApprove, payload);
  }

  getFeedbackDetails(feedbackId: number | string) {
    return this.commonApiService.get(
      `${APIURL.externalShareFeedbackDetails}/${feedbackId}`,
    );
  }

  approveVideo(videoId: number | string, payload: { is_approved: boolean }) {
    return this.commonApiService.post(
      `${APIURL.externalShareVideoApprove}/${videoId}`,
      payload,
    );
  }
}
